"use client";
/**
 * User-configurable top navigation. The nav is no longer a hardcoded list — it's
 * a tree the user arranges from Settings → Pages: show / hide, reorder, rename,
 * group, add link pages, and nest ONE level of sub-pages (rendered as a
 * second-row sub-tab bar under the top nav).
 *
 * Model: an ordered array of top-level {@link NavItem}s, each optionally holding
 * a `children` array (one level). Kinds:
 *   - "builtin"   — an app route that ships with the product (id === its href).
 *   - "link"      — a user-added link to any path (or external URL).
 *   - "workspace" — a user-added custom page rendered at /p/{slug} (Phase 2).
 *   - "group"     — a label-only parent (no route); clicking it opens its first child.
 *
 * The shipped arrangement is a few SECTIONS (2026-10-30, the owner: "we have
 * too many pages presented... bundle them"): a section is a group whose pages
 * show as the second row, or one page that has a tab bar of its own (Timing,
 * Options, Wealth, Settings), which stays a page so a section never stacks a
 * third row of tabs.
 *
 * Persisted to `mpp:nav:config:v2`, which the prefs-sync layer mirrors to the
 * server, so the arrangement follows the user across browsers. A saved `:v1`
 * (one flat tab per page, and the first-run snapshot's) is put into sections
 * once by `intoSections` and stored as `:v2`. A small zustand store keeps the
 * TopNav and the Settings editor in lock-step within a session.
 */
import { create } from "zustand";
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";

export type NavKind = "builtin" | "link" | "workspace" | "group";

/** How nested sub-pages render in the top nav. */
export type NavStyle = "subtabs" | "dropdown";

export type NavItem = {
  id: string;
  label: string;
  kind: NavKind;
  /** builtin / link: the route or URL. */
  href?: string;
  /** workspace: resolves to `/p/{slug}`. */
  slug?: string;
  /** link: open in a new tab. */
  external?: boolean;
  visible: boolean;
  /** One level of sub-pages (rendered as second-row sub-tabs). */
  children?: NavItem[];
};

const KEY = "mpp:nav:config:v2";
/** One flat tab per page, before the sections. Read once, never written. */
const V1_KEY = "mpp:nav:config:v1";
const STYLE_KEY = "mpp:nav:style:v1";

export function loadStyle(): NavStyle {
  return safeLocalStorageGet(STYLE_KEY) === "dropdown" ? "dropdown" : "subtabs";
}
export function saveStyle(style: NavStyle): void {
  try {
    safeLocalStorageSet(STYLE_KEY, style);
  } catch {
    /* ignore */
  }
}

/** The product's built-in pages, `id === href`, with the label and order each
 *  shipped under as a tab of its own, before the sections: a saved `:v1` names
 *  them so, and `intoSections` gives a page still under that label its
 *  section's label. */
const BUILTINS: ReadonlyArray<{ href: string; label: string }> = [
  { href: "/flow", label: "Flow" },
  { href: "/dashboard", label: "Dashboard" },
  { href: "/live-breadth", label: "Live Breadth" },
  // The morning read sits with the glance it follows. Its own top-level section,
  // like Live Breadth and Scanner — the old `/timing/daily-brief` URL redirects.
  { href: "/daily-brief", label: "Daily Brief" },
  { href: "/timing", label: "Market Timing" },
  // Promoted out of the Market Timing sub-tabs and parked right after the
  // section it came from. `/timing/outlook` redirects.
  { href: "/outlook", label: "Market Outlook and Journal" },
  { href: "/scanner", label: "Scanner" },
  { href: "/charts", label: "Charting" },
  { href: "/mtf", label: "Multi-Timeframe" },
  { href: "/multi-chart", label: "Multi-Chart" },
  { href: "/workspaces", label: "Workspaces" },
  { href: "/vpa", label: "VPA Analysis" },
  { href: "/fundamentals", label: "Fundamental Analysis" },
  { href: "/seasonality", label: "Seasonality Analysis" },
  { href: "/minervini", label: "Minervini" },
  { href: "/stockbee", label: "Stockbee" },
  { href: "/candlesticks", label: "Candlesticks" },
  { href: "/backtest", label: "Backtest Lab" },
  { href: "/quant", label: "Quant Lab" },
  { href: "/quant/regime", label: "Regime Lab" },
  { href: "/options", label: "Options" },
  { href: "/bets", label: "Market Bets" },
  { href: "/wealth", label: "Wealth and Trade Management" },
  { href: "/settings", label: "Settings" },
  { href: "/help", label: "Help" },
  { href: "/about", label: "About" },
];

/** Built-in pages that must always stay reachable (can't be hidden), so the user
 *  can never lock themselves out of the page editor. */
export const PROTECTED_IDS: ReadonlySet<string> = new Set(["/settings"]);

/**
 * The top bar's sections, in order. One with `pages` is a group (id
 * `group:*`, no route of its own: it opens its first shown page); one without
 * is a built-in page whose id is its href. Every page of `BUILTINS` is in
 * exactly one section.
 */
const SECTIONS: ReadonlyArray<{ id: string; label: string; pages?: ReadonlyArray<{ href: string; label: string }> }> = [
  {
    id: "group:today",
    label: "Today",
    pages: [
      { href: "/flow", label: "Flow" },
      { href: "/dashboard", label: "Dashboard" },
      { href: "/live-breadth", label: "Live Breadth" },
      { href: "/daily-brief", label: "Daily Brief" },
      { href: "/outlook", label: "Outlook and Journal" },
      { href: "/bets", label: "Market Bets" },
    ],
  },
  { id: "/timing", label: "Timing" },
  {
    id: "group:ideas",
    label: "Ideas",
    pages: [
      { href: "/scanner", label: "Scanner" },
      { href: "/minervini", label: "Minervini" },
      { href: "/stockbee", label: "Stockbee" },
      { href: "/candlesticks", label: "Candlesticks" },
    ],
  },
  {
    id: "group:analysis",
    label: "Analysis",
    pages: [
      { href: "/charts", label: "Charting" },
      { href: "/vpa", label: "VPA" },
      { href: "/fundamentals", label: "Fundamentals" },
      { href: "/seasonality", label: "Seasonality" },
      { href: "/mtf", label: "Multi-Timeframe" },
      { href: "/multi-chart", label: "Multi-Chart" },
      { href: "/workspaces", label: "Workspaces" },
    ],
  },
  { id: "/options", label: "Options" },
  {
    id: "group:labs",
    label: "Labs",
    pages: [
      { href: "/backtest", label: "Backtest Lab" },
      { href: "/quant", label: "Quant Lab" },
      { href: "/quant/regime", label: "Regime Lab" },
    ],
  },
  { id: "/wealth", label: "Wealth" },
  { id: "/settings", label: "Settings" },
  {
    id: "group:help",
    label: "Help",
    pages: [
      { href: "/help", label: "Help" },
      { href: "/about", label: "About" },
    ],
  },
];

/** The section a built-in page belongs to: a group's id, or the page itself. */
const SECTION_OF: ReadonlyMap<string, string> = new Map(
  SECTIONS.flatMap((s) => (s.pages ? s.pages.map((p) => [p.href, s.id] as const) : [[s.id, s.id] as const])),
);

/** The label each built-in shipped under before the sections, and Wealth's
 *  before it took Trade Management in. A saved label among these was never
 *  typed by the user. */
const OLD_LABELS: ReadonlyMap<string, ReadonlySet<string>> = new Map(
  BUILTINS.map((b) => [b.href, new Set(b.href === "/wealth" ? [b.label, "Wealth"] : [b.label])] as const),
);

/** Drawn at the right of the bar, beside the account controls, rather than
 *  among the sections: they are about the app, not the market. */
const UTILITY_IDS: ReadonlySet<string> = new Set(["/settings", "group:help", "/help", "/about"]);

export function isUtilityItem(item: NavItem): boolean {
  return UTILITY_IDS.has(item.id);
}

export function defaultNav(): NavItem[] {
  return SECTIONS.map((s) =>
    s.pages
      ? {
          id: s.id,
          label: s.label,
          kind: "group" as const,
          visible: true,
          children: s.pages.map((p) => ({ id: p.href, label: p.label, kind: "builtin" as const, href: p.href, visible: true })),
        }
      : { id: s.id, label: s.label, kind: "builtin" as const, href: s.id, visible: true },
  );
}

/**
 * A flat `:v1` arrangement in sections, once. Every built-in goes to its
 * section in the section's order, keeping whether it was shown and any label
 * the user typed (a label it shipped under becomes its section label). What
 * the user added keeps its place as nearly as sections allow: a page or link
 * joins the section of the first built-in after it in the bar, after that
 * section's own pages (a section that is one page gets it just before it, at
 * the top level); one with no built-in after it stays at the end. A group the
 * user made keeps the pages they added to it, gives its built-ins to their
 * sections, and goes where its first built-in's section is; one left empty
 * is dropped. A section whose every page is hidden is hidden too.
 */
export function intoSections(saved: NavItem[]): NavItem[] {
  const builtinById = new Map<string, NavItem>();
  const walk = (list: NavItem[]) =>
    list.forEach((it) => {
      if (it.kind === "builtin" && SECTION_OF.has(it.id)) builtinById.set(it.id, it);
      if (it.children) walk(it.children);
    });
  walk(saved);

  const labelOf = (id: string, label: string): string => {
    const own = builtinById.get(id)?.label;
    return own && !OLD_LABELS.get(id)?.has(own) ? own : label;
  };
  const pageOf = (href: string, label: string): NavItem => ({
    id: href,
    label: labelOf(href, label),
    kind: "builtin",
    href,
    visible: href === "/settings" ? true : (builtinById.get(href)?.visible ?? true),
  });

  // What the user added, each with the section it goes beside. A built-in's
  // children that are the user's own travel with it.
  const sectionAfter = (from: number): string | null => {
    for (let i = from; i < saved.length; i++) {
      const it = saved[i]!;
      const ids = [it.id, ...(it.children ?? []).map((c) => c.id)];
      const hit = ids.find((id) => SECTION_OF.has(id));
      if (hit) return SECTION_OF.get(hit)!;
    }
    return null;
  };
  const isOwn = (it: NavItem) => !(it.kind === "builtin" && SECTION_OF.has(it.id));
  const intoGroup = new Map<string, NavItem[]>();
  const beforeTop = new Map<string, NavItem[]>();
  const atEnd: NavItem[] = [];
  const pageChildren = new Map<string, NavItem[]>();
  const place = (section: string | null, it: NavItem, topOnly: boolean) => {
    if (!section) return void atEnd.push(it);
    const isGroup = SECTIONS.find((s) => s.id === section)?.pages;
    const into = isGroup && !topOnly ? intoGroup : beforeTop;
    into.set(section, [...(into.get(section) ?? []), it]);
  };
  saved.forEach((it, i) => {
    if (!isOwn(it)) {
      const own = (it.children ?? []).filter(isOwn);
      if (!own.length) return;
      const section = SECTION_OF.get(it.id)!;
      if (section === it.id) pageChildren.set(it.id, own);
      else own.forEach((c) => place(section, c, false));
      return;
    }
    if (it.kind === "group") {
      const own = (it.children ?? []).filter(isOwn);
      if (!own.length) return;
      const first = (it.children ?? []).find((c) => !isOwn(c));
      place(first ? SECTION_OF.get(first.id)! : sectionAfter(i + 1), { ...it, children: own }, true);
      return;
    }
    place(sectionAfter(i + 1), it, false);
  });

  const out: NavItem[] = [];
  for (const s of SECTIONS) {
    out.push(...(beforeTop.get(s.id) ?? []));
    if (!s.pages) {
      const page = pageOf(s.id, s.label);
      const kids = pageChildren.get(s.id);
      out.push(kids ? { ...page, children: kids } : page);
      continue;
    }
    const children = [...s.pages.map((p) => pageOf(p.href, p.label)), ...(intoGroup.get(s.id) ?? [])];
    out.push({ id: s.id, label: s.label, kind: "group", visible: children.some((c) => c.visible), children });
  }
  return [...out, ...atEnd];
}

/** Built-ins shipped after a `:v2` was saved: each into its section when that
 *  section is in the bar, else at the end. */
function addMissing(items: NavItem[]): NavItem[] {
  const present = allIds(items);
  let next = items;
  for (const s of SECTIONS) {
    for (const p of s.pages ?? [{ href: s.id, label: s.label }]) {
      if (present.has(p.href)) continue;
      const page: NavItem = { id: p.href, label: p.label, kind: "builtin", href: p.href, visible: true };
      const group = s.pages ? next.find((it) => it.id === s.id) : undefined;
      next = group
        ? next.map((it) => (it === group ? { ...it, children: [...(it.children ?? []), page] } : it))
        : [...next, page];
    }
  }
  return next;
}

/** The effective href a nav item points at: its own, its /p/{slug}, or (for a
 *  group) its first visible child's target. Null when there's nothing to open. */
export function hrefOf(item: NavItem): string | null {
  if (item.kind === "workspace" && item.slug) return `/p/${item.slug}`;
  if (item.href) return item.href;
  const child = item.children?.find((c) => c.visible);
  return child ? hrefOf(child) : null;
}

/** All ids present anywhere in the tree. */
function allIds(items: NavItem[]): Set<string> {
  const out = new Set<string>();
  const walk = (list: NavItem[]) =>
    list.forEach((it) => {
      out.add(it.id);
      if (it.children) walk(it.children);
    });
  walk(items);
  return out;
}

/** Defensive: coerce a persisted item into a valid NavItem shape. */
function sanitize(raw: unknown): NavItem | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || typeof r.label !== "string") return null;
  const kind = (["builtin", "link", "workspace", "group"] as const).includes(r.kind as NavKind)
    ? (r.kind as NavKind)
    : "link";
  const children = Array.isArray(r.children)
    ? (r.children.map(sanitize).filter(Boolean) as NavItem[])
    : undefined;
  return {
    id: r.id,
    label: r.label,
    kind,
    href: typeof r.href === "string" ? r.href : undefined,
    slug: typeof r.slug === "string" ? r.slug : undefined,
    external: r.external === true,
    visible: r.visible !== false,
    children: children && children.length ? children : undefined,
  };
}

/** Daily Brief's canonical id, now that it is its own top-level section. */
const DAILY_BRIEF_ID = "/daily-brief";

/** Market Outlook's canonical id, now that it is its own top-level section. */
const OUTLOOK_ID = "/outlook";

/** Put a newly-shipped builtin directly after Market Timing. */
function insertAfterTiming(items: NavItem[], item: NavItem): NavItem[] {
  const i = items.findIndex((it) => it.id === "/timing");
  const at = i >= 0 ? i + 1 : items.length;
  return [...items.slice(0, at), item, ...items.slice(at)];
}

/**
 * Market Outlook left the Market Timing sub-tabs and the `/timing/*` route with
 * it, so a saved nav may name it by either id. Normalise to `/outlook` and park
 * it right after the section it came from, mirroring the two promoters below.
 */
function promoteOutlook(items: NavItem[]): NavItem[] {
  const isOl = (it: NavItem) =>
    it.id === OUTLOOK_ID ||
    it.id === "/timing/outlook" ||
    it.href === OUTLOOK_ID ||
    it.href === "/timing/outlook";

  let plucked: NavItem | null = null;
  const strip = (list: NavItem[]): NavItem[] =>
    list.flatMap((it) => {
      if (isOl(it)) {
        if (!plucked) {
          plucked = {
            ...it,
            id: OUTLOOK_ID,
            href: OUTLOOK_ID,
            kind: "builtin",
            label: "Market Outlook and Journal",
            children: undefined,
          };
        }
        return [];
      }
      if (!it.children?.length) return [it];
      const children = strip(it.children);
      return [{ ...it, children: children.length ? children : undefined }];
    });

  const next = strip(items);
  const item: NavItem = plucked ?? {
    id: OUTLOOK_ID,
    label: "Market Outlook and Journal",
    kind: "builtin",
    href: OUTLOOK_ID,
    visible: true,
  };
  return insertAfterTiming(next, item);
}

/** Put a newly-shipped builtin directly after Dashboard instead of appending.
 *  Appending is right for most pages; it is wrong for one that belongs to the
 *  first thirty seconds of the morning. */
function insertAfterDashboard(items: NavItem[], item: NavItem): NavItem[] {
  const i = items.findIndex((it) => it.id === "/dashboard");
  const at = i >= 0 ? i + 1 : items.length;
  return [...items.slice(0, at), item, ...items.slice(at)];
}

/**
 * Daily Brief moved twice: out of the Market Timing sub-tabs, then out of the
 * `/timing/*` route itself (staying under `/timing` meant inheriting that
 * section's tab bar — nine siblings it is no longer one of). A saved nav can
 * therefore name it by either id, and the short-lived `/timing/daily-brief`
 * builtin may already be sitting in one. Normalise both to `/daily-brief` and
 * park it after Dashboard, exactly as `promoteLiveBreadth` does.
 */
function promoteDailyBrief(items: NavItem[]): NavItem[] {
  const isDb = (it: NavItem) =>
    it.id === DAILY_BRIEF_ID ||
    it.id === "/timing/daily-brief" ||
    it.href === DAILY_BRIEF_ID ||
    it.href === "/timing/daily-brief";

  let plucked: NavItem | null = null;
  const strip = (list: NavItem[]): NavItem[] =>
    list.flatMap((it) => {
      if (isDb(it)) {
        if (!plucked) {
          plucked = {
            ...it,
            id: DAILY_BRIEF_ID,
            href: DAILY_BRIEF_ID,
            kind: "builtin",
            label: it.label || "Daily Brief",
            children: undefined,
          };
        }
        return [];
      }
      if (!it.children?.length) return [it];
      const children = strip(it.children);
      return [{ ...it, children: children.length ? children : undefined }];
    });

  const next = strip(items);
  const item: NavItem = plucked ?? {
    id: DAILY_BRIEF_ID,
    label: "Daily Brief",
    kind: "builtin",
    href: DAILY_BRIEF_ID,
    visible: true,
  };
  return insertAfterDashboard(next, item);
}

/** Live Breadth used to sit under Market Timing (and sometimes Dashboard) —
 *  promote any leftover nested entry to the canonical top-level builtin. */
function promoteLiveBreadth(items: NavItem[]): NavItem[] {
  const isLb = (it: NavItem) =>
    it.id === "/live-breadth" ||
    it.id === "/timing/live-breadth" ||
    it.href === "/live-breadth" ||
    it.href === "/timing/live-breadth";

  let plucked: NavItem | null = null;
  const strip = (list: NavItem[]): NavItem[] =>
    list.flatMap((it) => {
      if (isLb(it)) {
        if (!plucked) {
          plucked = {
            ...it,
            id: "/live-breadth",
            href: "/live-breadth",
            kind: "builtin",
            label: it.label || "Live Breadth",
            children: undefined,
          };
        }
        return [];
      }
      if (!it.children?.length) return [it];
      const children = strip(it.children);
      return [{ ...it, children: children.length ? children : undefined }];
    });

  let next = strip(items);
  const top = next.find((it) => it.id === "/live-breadth");
  if (top) {
    top.href = "/live-breadth";
    top.kind = "builtin";
    return next;
  }
  const item: NavItem = plucked ?? {
    id: "/live-breadth",
    label: "Live Breadth",
    kind: "builtin",
    href: "/live-breadth",
    visible: true,
  };
  item.id = "/live-breadth";
  item.href = "/live-breadth";
  item.kind = "builtin";
  const dashIdx = next.findIndex((it) => it.id === "/dashboard");
  const insertAt = dashIdx >= 0 ? dashIdx + 1 : next.length;
  return [...next.slice(0, insertAt), item, ...next.slice(insertAt)];
}

/**
 * Trade Management became a Wealth tab (`/wealth/trades`) on 2026-10-01, and
 * Wealth was renamed "Wealth and Trade Management". A saved nav still names the
 * old `/trades` page: drop it (the route redirects, so nothing breaks either
 * way), and rename Wealth only where its label is still the old default, never
 * over a label the user typed.
 */
function foldTradesIntoWealth(items: NavItem[]): NavItem[] {
  const strip = (list: NavItem[]): NavItem[] =>
    list.flatMap((it) => {
      if (it.kind === "builtin" && (it.id === "/trades" || it.href === "/trades")) return [];
      const renamed = it.kind === "builtin" && it.id === "/wealth" && it.label === "Wealth" ? { ...it, label: "Wealth and Trade Management" } : it;
      if (!renamed.children?.length) return [renamed];
      const children = strip(renamed.children);
      return [{ ...renamed, children: children.length ? children : undefined }];
    });
  return strip(items);
}

/** A stored arrangement, or null when there is none worth reading. */
function readSaved(key: string): NavItem[] | null {
  const raw = safeLocalStorageGet(key);
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  if (!Array.isArray(parsed)) return null;
  const saved = parsed.map(sanitize).filter(Boolean) as NavItem[];
  return saved.length ? saved : null;
}

/** Force-restore any hidden protected built-in, at any depth. */
function restoreProtected(items: NavItem[]): NavItem[] {
  return items.map((it) => ({
    ...it,
    visible: PROTECTED_IDS.has(it.id) ? true : it.visible,
    children: it.children ? restoreProtected(it.children) : undefined,
  }));
}

/** Load the saved arrangement, merged with the built-in defaults so newly-shipped
 *  pages appear (in their section, visible) and protected pages are never lost.
 *  A `:v1` with no `:v2` beside it is put into sections and stored as `:v2`. */
export function loadNav(): NavItem[] {
  const def = defaultNav();
  try {
    const saved = readSaved(KEY);
    if (saved) return restoreProtected(addMissing(saved));
    const old = readSaved(V1_KEY);
    if (!old) return def;
    // The promoters bring a `:v1` written before the pages moved to their
    // present addresses up to those addresses; `intoSections` then places them.
    const next = intoSections(foldTradesIntoWealth(promoteOutlook(promoteLiveBreadth(promoteDailyBrief(old)))));
    saveNav(next);
    return next;
  } catch {
    return def;
  }
}

export function saveNav(items: NavItem[]): void {
  try {
    safeLocalStorageSet(KEY, JSON.stringify(items));
  } catch {
    /* ignore */
  }
}

type NavStore = {
  items: NavItem[];
  style: NavStyle;
  hydrated: boolean;
  hydrate: () => void;
  update: (items: NavItem[]) => void;
  /** Hide a page by id (top-level or nested) — the quick × in the nav. No-op on
   *  protected ids. It's not deleted, just `visible:false`; restore in Settings. */
  hide: (id: string) => void;
  setStyle: (style: NavStyle) => void;
  reset: () => void;
};

/** Shared store so the TopNav re-renders when the Settings editor saves. Starts
 *  from the pure defaults (SSR-safe); `hydrate()` pulls the saved arrangement on
 *  the client. */
export const useNavConfig = create<NavStore>((set, get) => ({
  items: defaultNav(),
  style: "subtabs",
  hydrated: false,
  hydrate: () => {
    if (get().hydrated) return;
    set({ items: loadNav(), style: loadStyle(), hydrated: true });
  },
  update: (items) => {
    saveNav(items);
    set({ items });
  },
  hide: (id) => {
    if (PROTECTED_IDS.has(id)) return;
    const next = JSON.parse(JSON.stringify(get().items)) as NavItem[];
    const walk = (list: NavItem[]) => {
      for (const it of list) {
        if (it.id === id) it.visible = false;
        if (it.children) walk(it.children);
      }
    };
    walk(next);
    saveNav(next);
    set({ items: next });
  },
  setStyle: (style) => {
    saveStyle(style);
    set({ style });
  },
  reset: () => {
    const def = defaultNav();
    saveNav(def);
    set({ items: def });
  },
}));
